import type { i18n as I18nInstance } from 'i18next';
import i18n from '@/i18n';
import { I18N_EVENTS, I18N_STORE_EVENTS } from '@/i18n/events';

type I18nStoreEvent = (typeof I18N_STORE_EVENTS)[number];

type DashboardI18nAdapter = {
  t: (key: string, options?: Record<string, unknown>) => string;
  getResolvedLanguage: () => string;
  subscribe: (onStoreChange: () => void) => () => void;
};

type I18nStore = {
  on: (event: I18nStoreEvent, callback: (...args: unknown[]) => void) => void;
  off: (event: I18nStoreEvent, callback: (...args: unknown[]) => void) => void;
};

export function createI18nAdapter(
  instance: I18nInstance,
): DashboardI18nAdapter {
  return {
    t: instance.t.bind(instance),
    getResolvedLanguage: () => instance.resolvedLanguage || 'en',
    subscribe: (onStoreChange: () => void) => {
      const store = instance.store as I18nStore | undefined;

      const handleChange = () => {
        onStoreChange();
      };

      // Language/load events keep shared UI in sync on startup and on locale switches.
      I18N_EVENTS.forEach((event) => {
        instance.on(event, handleChange);
      });
      I18N_STORE_EVENTS.forEach((event) => {
        store?.on(event, handleChange);
      });

      return () => {
        I18N_EVENTS.forEach((event) => {
          instance.off(event, handleChange);
        });
        I18N_STORE_EVENTS.forEach((event) => {
          store?.off(event, handleChange);
        });
      };
    },
  };
}

export const dashboardI18nAdapter = createI18nAdapter(i18n);
